import React, { useState } from "react";
import { Play, ExternalLink, Share2 } from "lucide-react";
import { toast } from "sonner";
import { VIDEOS, linkDoVideo } from "./conteudo/videos";

/**
 * Um vídeo da playlist de tutoriais: começa como miniatura (não carrega nada
 * do YouTube até o clique) e vira o player ali mesmo. Ao lado (ou abaixo, no
 * horizontal), abrir no YouTube e compartilhar o link.
 *
 * Quase todos são Shorts (9:16): o card é em pé, com a miniatura vertical do
 * YouTube (`oardefault`). O player é o youtube.com — o youtube-nocookie fica
 * com a tela preta em Shorts (testado em 23/10/2026). Sem o Referer o YouTube
 * recusa vídeo embutido (erro 153), por isso o referrerPolicy.
 */
export default function VideoTutorial({ id }) {
  const [tocando, setTocando] = useState(false);
  const video = VIDEOS[id];
  if (!video) return null;
  const link = linkDoVideo(id);
  const emPe = !video.horizontal;

  // Celular: menu de compartilhar do aparelho (WhatsApp etc.). Computador, ou
  // se o menu falhar (app do computador pode não ter): copia o link.
  const compartilhar = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: video.titulo, text: `Tutorial Núcleo: ${video.titulo}`, url: link });
        return;
      } catch (e) {
        if (e?.name === "AbortError") return; // fechou o menu de compartilhar
      }
    }
    try {
      await navigator.clipboard.writeText(link);
      toast.success("Link do vídeo copiado");
    } catch {
      toast.error("Não deu para copiar. Use o botão Abrir no YouTube e copie o link de lá.");
    }
  };

  const player = (
    <div className={`relative bg-black flex-shrink-0 ${emPe ? "w-full sm:w-[220px] max-w-[260px] aspect-[9/16] rounded-lg overflow-hidden" : "aspect-video"}`}>
      {tocando ? (
        <iframe
          className="absolute inset-0 w-full h-full"
          src={`https://www.youtube.com/embed/${id}?autoplay=1&rel=0&playsinline=1`}
          title={video.titulo}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          referrerPolicy="strict-origin-when-cross-origin"
          allowFullScreen
        />
      ) : (
        <button
          onClick={() => setTocando(true)}
          className="absolute inset-0 w-full h-full group"
          aria-label={`Assistir: ${video.titulo}`}
        >
          <img
            src={`https://i.ytimg.com/vi/${id}/${emPe ? "oardefault" : "mqdefault"}.jpg`}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity"
          />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-14 h-14 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-lg group-hover:scale-105 transition-transform">
              <Play className="w-6 h-6 ml-0.5" fill="currentColor" />
            </span>
          </span>
          <span className="absolute bottom-2 right-2 text-[11px] font-semibold px-1.5 py-0.5 rounded bg-black/75 text-white">
            {video.duracao}
          </span>
        </button>
      )}
    </div>
  );

  const acoes = (
    <>
      <a
        href={link}
        target="_blank"
        rel="noopener noreferrer"
        className="min-h-[36px] px-3 rounded-lg text-xs font-semibold bg-muted hover:bg-muted/70 transition-colors flex items-center justify-center gap-1.5"
      >
        <ExternalLink className="w-3.5 h-3.5" /> Abrir no YouTube
      </a>
      <button
        onClick={compartilhar}
        className="min-h-[36px] px-3 rounded-lg text-xs font-semibold bg-primary/10 text-primary hover:bg-primary/20 transition-colors flex items-center justify-center gap-1.5"
      >
        <Share2 className="w-3.5 h-3.5" /> Compartilhar
      </button>
    </>
  );

  if (emPe) {
    return (
      <div className="rounded-lg border bg-background p-3 flex flex-col sm:flex-row gap-3 sm:gap-4 max-w-xl">
        {player}
        <div className="flex flex-col gap-2 min-w-0 sm:py-1">
          <span className="text-[10px] font-bold uppercase tracking-wider text-primary">Vídeo · Shorts · {video.duracao}</span>
          <span className="text-sm font-semibold leading-snug">{video.titulo}</span>
          <div className="flex flex-wrap sm:flex-col gap-2 sm:mt-auto">{acoes}</div>
        </div>
      </div>
    );
  }
  return (
    <div className="rounded-lg border bg-background overflow-hidden max-w-xl">
      {player}
      <div className="flex flex-wrap items-center gap-2 px-3 py-2.5">
        <span className="text-sm font-medium flex-1 min-w-[10rem]">{video.titulo}</span>
        {acoes}
      </div>
    </div>
  );
}
